export const parseStatValue = (value) => {
    if (value == null || value === '—' || value === '') {
        return 0;
    }

    const str = String(value).trim().replace(/%$/, '');
    const suffixMatch = str.match(/^([\d.]+)\s*([KMB])$/i);

    if (suffixMatch) {
        const num = parseFloat(suffixMatch[1]);
        const multipliers = { K: 1_000, M: 1_000_000, B: 1_000_000_000 };
        return num * (multipliers[suffixMatch[2].toUpperCase()] || 1);
    }

    // Avoid treating comma-separated series as a single number.
    if (str.includes(',') && !/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(str)) {
        return 0;
    }

    const parsed = parseFloat(str.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

// Extracts the leading count from either a plain value ("1,234") or a
// count-with-share value ("1,234 (56%)").
export const parseStatCount = (value) => {
    const match = String(value ?? '').trim().match(/^([\d,.]+)/);
    return match ? parseStatValue(match[1]) : 0;
};

export const parseDailyStatSeries = (value, expectedLength = 30) => {
    if (value == null || value === '' || value === '—') {
        return Array.from({ length: expectedLength }, () => 0);
    }

    const parts = String(value)
        .split(',')
        .map((part) => {
            const parsed = Number.parseInt(part.trim(), 10);
            return Number.isFinite(parsed) ? parsed : 0;
        });

    if (parts.length >= expectedLength) {
        return parts.slice(parts.length - expectedLength);
    }

    return [...Array.from({ length: expectedLength - parts.length }, () => 0), ...parts];
};

export const buildLastNDayLabels = (days = 30, now = new Date()) => {
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    const labels = [];

    for (let offset = days - 1; offset >= 0; offset -= 1) {
        const date = new Date(now);
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() - offset);
        labels.push(formatter.format(date));
    }

    return labels;
};

export const buildAppointmentsDailyRatio = (getStat, days = 30) => {
    const labels = buildLastNDayLabels(days);
    const totals = parseDailyStatSeries(getStat('APPOINTMENTS_DAILY_TOTAL_LAST_30_DAYS'), days);
    const confirmed = parseDailyStatSeries(getStat('APPOINTMENTS_DAILY_CONFIRMED_LAST_30_DAYS'), days);

    const points = labels.map((label, index) => ({
        label,
        total: totals[index] || 0,
        confirmed: confirmed[index] || 0,
    }));

    const summary = points.reduce(
        (acc, point) => {
            acc.total += point.total;
            acc.confirmed += point.confirmed;
            return acc;
        },
        { total: 0, confirmed: 0 }
    );

    return { labels, points, summary };
};

const CONTACT_PERCENT_STATS = {
    PATIENTS_EMAIL_PERCENT: 'PATIENTS_WITH_EMAIL',
    PATIENTS_CELL_PHONE_PERCENT: 'PATIENTS_WITH_CELL_PHONE',
    PATIENTS_HOME_PHONE_PERCENT: 'PATIENTS_WITH_HOME_PHONE',
};

export const formatPercentage = (part, total, { decimals = 1 } = {}) => {
    const numerator = parseStatValue(part);
    const denominator = parseStatValue(total);

    if (!denominator) {
        return '—';
    }

    const percent = (numerator / denominator) * 100;
    return `${percent.toFixed(decimals)}%`;
};

export const resolveStatValue = (statsMap = {}, key) => {
    if (statsMap[key] != null && statsMap[key] !== '') {
        return statsMap[key];
    }

    const sourceKey = CONTACT_PERCENT_STATS[key];
    if (!sourceKey) {
        return '—';
    }

    return formatPercentage(statsMap[sourceKey], statsMap.TOTAL_PATIENTS);
};

export const hasChartData = (series = []) =>
    series.some((item) => parseStatValue(item.value) > 0);
